import { Component, EventEmitter, Input, Output } from '@angular/core';

@Component({
    selector: 'ham-pagination',
    template: `
        <div class="pagination">
            <span
                >Showing <strong>{{ startItem }}-{{ endItem }}</strong> of <strong>{{ totalItems }}</strong></span
            >
            <ul class="page-nav">
                <li (click)="previousPage()" [class.disabled]="currentPage === 1">
                    <span class="ham-icon">
                        <i class="chevron-left-icon"></i>
                    </span>
                </li>
                <li *ngIf="showFirstPage" (click)="goToPage(1)">1</li>
                <li *ngIf="showEllipsisStart">...</li>
                <li
                    *ngFor="let page of getDisplayedPages()"
                    (click)="goToPage(page)"
                    [class.active]="page === currentPage"
                >
                    {{ page }}
                </li>
                <li *ngIf="showEllipsisEnd">...</li>
                <li *ngIf="showLastPage" (click)="goToPage(totalPages)">{{ totalPages }}</li>
                <li (click)="nextPage()" [class.disabled]="currentPage === totalPages">
                    <span class="ham-icon">
                        <i class="chevron-right-icon"></i>
                    </span>
                </li>
            </ul>
        </div>
    `,
    styleUrls: ['./pagination.component.scss'],
})
export class HamPaginationComponent {
    @Input() currentPage: number = 1;
    @Input() pageSize: number = 5;
    @Input() totalItems: number = 0;
    @Output() pageChange = new EventEmitter<number>();

    totalPages: number = 1;
    showEllipsisStart: boolean = false;
    showEllipsisEnd: boolean = false;
    showFirstPage: boolean = false;
    showLastPage: boolean = false;

    ngOnChanges() {
        this.totalPages = Math.ceil(this.totalItems / this.pageSize);
        this.updatePaginationControls();
    }

    get startItem(): number {
        return (this.currentPage - 1) * this.pageSize + 1;
    }

    get endItem(): number {
        return Math.min(this.currentPage * this.pageSize, this.totalItems);
    }

    nextPage() {
        if (this.currentPage < this.totalPages) {
            this.currentPage++;
            this.pageChange.emit(this.currentPage);
            this.updatePaginationControls();
        }
    }

    previousPage() {
        if (this.currentPage > 1) {
            this.currentPage--;
            this.pageChange.emit(this.currentPage);
            this.updatePaginationControls();
        }
    }

    goToPage(page: number) {
        this.currentPage = page;
        this.pageChange.emit(this.currentPage);
        this.updatePaginationControls();
    }

    getDisplayedPages(): number[] {
        const pages: number[] = [];
        if (this.totalPages <= 4) {
            for (let i = 1; i <= this.totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(this.currentPage);

            if (this.currentPage > 1) {
                pages.unshift(this.currentPage - 1);
            }

            if (this.currentPage < this.totalPages) {
                pages.push(this.currentPage + 1);
            }

            if (this.currentPage === 1) {
                pages.push(3);
            } else if (this.currentPage === this.totalPages) {
                pages.unshift(this.totalPages - 2);
            }

            this.showEllipsisStart = this.currentPage > 3;
            this.showFirstPage = this.currentPage > 2;
            this.showEllipsisEnd = this.currentPage < this.totalPages - 2;
            this.showLastPage = this.currentPage < this.totalPages - 1;
        }
        return pages;
    }

    updatePaginationControls() {
        if (this.totalPages <= 4) {
            // For small page counts, just show all pages and no extras
            this.showEllipsisStart = false;
            this.showFirstPage = false;
            this.showEllipsisEnd = false;
            this.showLastPage = false;
        } else {
            // For larger page counts, use the existing logic
            this.showEllipsisStart = this.currentPage > 3;
            this.showFirstPage = this.currentPage > 2;
            this.showEllipsisEnd = this.currentPage < this.totalPages - 2;
            this.showLastPage = this.currentPage < this.totalPages - 1;
        }
    }
}
